import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleApiError } from "../../../Utils/thunkHelpers"
import * as services from "../services"
import { PAYMENT_MATCHING_SLICE_NAME } from "./paymentMatching.constants"

/* ------------- Table ------------- */

export const getPaymentMatching = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getPaymentMatching(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getPaymentMatchingSummary = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching_summary`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getPaymentMatchingSummary(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// The table stays usable without its filter options, so a failure here must not raise a toast on
// top of the one the table request would already have shown.
export const getPaymentMatchingFilterOptions = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching_filter_options`,
  async (params, { rejectWithValue }) => {
    const response = await services.getPaymentMatchingFilterOptions(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

/* ------------- Activity log ------------- */

export const getPaymentMatchingLogs = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching_logs`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getPaymentMatchingLogs(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// The log's User dropdown degrades to an empty list rather than to a toast.
export const getPaymentMatchingLogUsers = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching_log_users`,
  async (params, { rejectWithValue }) => {
    const response = await services.getPaymentMatchingLogUsers(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

/* ------------- Editing ------------- */

// Editing failures are handled by the caller rather than by the global error toast: a rename
// answers 409 with the conflict dialog's contents, and an invalid icon has to name the failing rule
// inside the popover. Both reach the page through `.unwrap()` rejecting with the response body,
// which is what the `onError` callback in the payload used to receive.

// The group lookup keeps no state of its own — the page reads the resolved group straight off
// `.unwrap()` to build its confirmation, exactly as the legacy action did through `onSuccess`.
export const getPaymentMatchingGroup = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching_group`,
  async (params, { rejectWithValue }) => {
    const response = await services.getPaymentMatchingGroup(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

export const getPaymentMatchingIcons = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/get_payment_matching_icons`,
  async (params, { rejectWithValue }) => {
    const response = await services.getPaymentMatchingIcons(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

export const renamePaymentMatching = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/rename_payment_matching`,
  async (params, { rejectWithValue }) => {
    const response = await services.renamePaymentMatching(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

export const setPaymentMatchingIcon = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/set_payment_matching_icon`,
  async (params, { rejectWithValue }) => {
    const response = await services.setPaymentMatchingIcon(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

export const uploadPaymentMatchingIcon = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/upload_payment_matching_icon`,
  async (params, { rejectWithValue }) => {
    const response = await services.uploadPaymentMatchingIcon(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

// Answers 204 with no body, which apisauce still reports as `ok`.
export const deletePaymentMatchingIcon = createAsyncThunk(
  `${PAYMENT_MATCHING_SLICE_NAME}/delete_payment_matching_icon`,
  async (params, { rejectWithValue }) => {
    const response = await services.deletePaymentMatchingIcon(params)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)
